import { useState, type ReactNode } from 'react';
import type { IDockviewPanelProps } from 'dockview-react';
import { formatServerTime, usePrefsValue, useServerTime } from '@adiox/ui';
import { MarketWatch } from '../../market/MarketWatch';
import { SymbolDetails } from '../../market/SymbolDetails';
import { TradingTiles } from '../../market/TradingTiles';
import { TickChart } from '../../chart/TickChart';
import { type ColumnId } from '../../market/columns';

/** Until the user picks columns (right-click, Columns…), the classic list shows what the terminal shows by default. */
const CLASSIC_COLUMNS: ColumnId[] = ['bid', 'ask', 'change'];

/** The small × of a docked window's title bar or the Toolbox strip. */
function CloseButton({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Close ${label}`}
      title="Close"
      onClick={onClose}
      className="classic-close grid h-4 w-4 place-items-center leading-none"
    >
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
        <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    </button>
  );
}

/**
 * A docked window of the classic terminal: a title bar with a close button, the content, and (for windows with several
 * views, like Market Watch) small tabs underneath.
 */
export function WindowFrame<T extends string>({
  title,
  label,
  onClose,
  tabs,
  tab,
  onTab,
  children,
}: {
  title: ReactNode;
  /** The window's name, for the close button and assistive technology. */
  label: string;
  onClose: () => void;
  tabs?: readonly { id: T; label: string }[];
  tab?: T;
  onTab?: (id: T) => void;
  children: ReactNode;
}) {
  return (
    <div className="classic-window flex h-full flex-col bg-panel" role="region" aria-label={label}>
      <div className="classic-titlebar flex shrink-0 items-center justify-between">
        <span className="truncate">{title}</span>
        <CloseButton label={label} onClose={onClose} />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      {tabs && (
        <div role="tablist" aria-label={`${label} views`} className="classic-subtabs flex shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === tab}
              onClick={() => onTab?.(t.id)}
              className="classic-subtab"
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The Toolbox's windows (Trade, History, …) have a vertical "Toolbox" strip with a close button at their left. */
export function ToolboxFrame({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="flex h-full">
      <div className="classic-toolbox-strip flex w-[18px] shrink-0 flex-col items-center gap-2 py-1">
        <CloseButton label="Toolbox" onClose={onClose} />
        <span className="classic-vertical">Toolbox</span>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

const MW_TABS = [
  { id: 'symbols', label: 'Symbols' },
  { id: 'details', label: 'Details' },
  { id: 'trading', label: 'Trading' },
  { id: 'ticks', label: 'Ticks' },
] as const;
type MwTab = (typeof MW_TABS)[number]['id'];

/** Market Watch as the classic terminal shows it: the server time in the title, four views underneath. */
export function ClassicMarketWatch({ api }: IDockviewPanelProps) {
  const time = useServerTime();
  const [tab, setTab] = useState<MwTab>('symbols');
  const chosen = usePrefsValue((s) => s.data.marketWatchColumns) !== undefined;
  return (
    <WindowFrame
      title={`Market Watch: ${time ? formatServerTime(time) : '--:--:--'}`}
      label="Market Watch window"
      onClose={() => api.close()}
      tabs={MW_TABS}
      tab={tab}
      onTab={setTab}
    >
      {tab === 'symbols' && <MarketWatch classic columns={chosen ? undefined : CLASSIC_COLUMNS} />}
      {tab === 'details' && <SymbolDetails />}
      {tab === 'trading' && <TradingTiles />}
      {tab === 'ticks' && <TickChart />}
    </WindowFrame>
  );
}
